import { useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import {
  AttachmentSelector as StreamAttachmentSelector,
  SendButton as StreamSendButton,
  useMessageComposer,
} from "stream-chat-react";
import type { SendButtonProps } from "stream-chat-react";
import { FilePlus, Loader2 } from "lucide-react";

import { FilesPickerModal } from "@/features/announcements/components/FilesPickerModal";
import type { SharepointFile } from "@/features/files/types/sharepoint";
import { linkSharepointFileToChat } from "@/shared/services/uploadService";

export type PendingSharepointAttachment = {
  attachment_id: string;
  type: "sharepoint_file";
  title: string;
  name: string;
  fallback: string;
  mime_type?: string;
  file_type?: string;
  file_size?: number;
  channel_id: string;
};

const normalizeFileSize = (value: unknown): number | undefined => {
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) return undefined;
  return Math.max(0, Math.trunc(numeric));
};

const toStreamSharepointAttachment = (
  attachment: any,
  file: SharepointFile,
  channelId: string,
): PendingSharepointAttachment & { localMetadata: { id: string; uploadState: "finished" } } => {
  const fileSize = normalizeFileSize(attachment.file_size ?? file.file_size);
  return {
    attachment_id: String(attachment.attachment_id),
    type: "sharepoint_file",
    title: attachment.display_name || file.display_name,
    name: attachment.display_name || file.display_name,
    fallback: attachment.display_name || file.display_name,
    mime_type: attachment.file_type || file.file_type,
    file_type: attachment.file_type || file.file_type,
    ...(fileSize !== undefined ? { file_size: fileSize } : {}),
    channel_id: channelId,
    localMetadata: {
      id: `sharepoint-${attachment.attachment_id}`,
      uploadState: "finished",
    },
  };
};

export const SharepointAttachmentSelector = ({
  channelId,
  pendingAttachments,
  setPendingAttachments,
}: {
  channelId: string | null;
  pendingAttachments: PendingSharepointAttachment[];
  setPendingAttachments: Dispatch<SetStateAction<PendingSharepointAttachment[]>>;
}) => {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const messageComposer = useMessageComposer();

  useEffect(() => {
    const unsubscribe = messageComposer.attachmentManager.state.subscribe((nextValue: any) => {
      const remainingIds = new Set(
        (nextValue?.attachments || []).map((attachment: any) => attachment?.localMetadata?.id),
      );
      if (pendingAttachments.length > 0) {
        const stillPending = pendingAttachments.filter((attachment) =>
          remainingIds.has(`sharepoint-${attachment.attachment_id}`),
        );
        if (stillPending.length !== pendingAttachments.length) {
          setPendingAttachments(stillPending);
        }
      }
    });
    return unsubscribe;
  }, [messageComposer, pendingAttachments, setPendingAttachments]);

  const handleSelect = async (files: SharepointFile[]) => {
    if (!channelId || files.length === 0) return;
    setLinking(true);
    try {
      const linked = await Promise.all(
        files.map(async (file) => {
          const attachment = await linkSharepointFileToChat(file.id, channelId);
          return toStreamSharepointAttachment(attachment, file, channelId);
        }),
      );
      messageComposer.attachmentManager.upsertAttachments(linked as any);
      setPendingAttachments((prev) => [
        ...prev,
        ...linked.map(({ localMetadata, ...attachment }) => attachment),
      ]);
    } finally {
      setLinking(false);
    }
  };

  return (
    <>
      <StreamAttachmentSelector />
      <button
        type="button"
        className="str-chat__attachment-selector__menu-button"
        aria-label={t("chat.attach_from_files")}
        title={t("chat.attach_from_files")}
        disabled={!channelId || linking}
        onClick={() => setPickerOpen(true)}
      >
        {linking ? <Loader2 className="h-5 w-5 animate-spin" /> : <FilePlus className="h-5 w-5" />}
        {pendingAttachments.length > 0 && (
          <span className="ml-1 text-[10px] font-semibold">
            {pendingAttachments.length}
          </span>
        )}
      </button>
      <FilesPickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleSelect}
      />
    </>
  );
};

export const SharepointSendButton = ({
  pendingAttachments,
  setPendingAttachments,
  sendMessage,
  ...props
}: SendButtonProps & {
  pendingAttachments: PendingSharepointAttachment[];
  setPendingAttachments: Dispatch<SetStateAction<PendingSharepointAttachment[]>>;
}) => {
  const handleSend: SendButtonProps["sendMessage"] = (event) => {
    sendMessage(event);
    if (pendingAttachments.length > 0) {
      setPendingAttachments([]);
    }
  };

  return <StreamSendButton {...props} sendMessage={handleSend} />;
};
